import React, { useEffect, useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import useMeasure from "utils/hooks/useMeasure";
import { hexToRGB } from "@shared/utils/misc";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

export interface BarPoint {
  /** Unix seconds (the start of the bucket). */
  time: number;
  value: number;
}

interface BarsChartProps {
  points: BarPoint[];
  height: number;
  /** Whole-number guides (counts). */
  integer?: boolean;
  formatAxisValue: (value: number) => string;
  formatTick: (time: number) => string;
  /** The time under the crosshair, on the time axis. */
  formatActiveTime: (time: number) => string;
  ariaLabel: string;
  /** The bar under the crosshair (undefined when it leaves). */
  onActiveChange?: (point?: BarPoint) => void;
}

/**
 * Quiet bars in the accent colour on dotted guides, drawn like the
 * dashboard's balance chart: value guides on the right, four time labels
 * underneath and a crosshair (pointer or arrow keys) that marks a bar and its
 * time; the panel's headline reads out the value.
 */
const BarsChart = ({
  points,
  height,
  integer,
  formatAxisValue,
  formatTick,
  formatActiveTime,
  ariaLabel,
  onActiveChange,
}: BarsChartProps) => {
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const [measureRef, size] = useMeasure<HTMLDivElement>();
  const width = size?.width ?? 0;
  const [active, setActive] = useState<number>();

  const plotWidth = Math.max(0, width - AXIS_WIDTH);
  const plotHeight = height - TOP - BOTTOM;

  const shape = useMemo(() => {
    if (!points.length || !plotWidth) return undefined;
    const values = points.map((point) => point.value);
    const { min, max, step } = getScale(
      Math.min(0, ...values),
      Math.max(0, ...values),
      integer
    );
    const y = (value: number) =>
      TOP + (1 - (value - min) / (max - min)) * plotHeight;
    const band = plotWidth / points.length;
    const barWidth = Math.max(2, Math.min(band * 0.62, 28));
    const bars = points.map((point, index) => {
      const top = y(Math.max(0, point.value));
      const bottom = y(Math.min(0, point.value));
      return {
        center: band * (index + 0.5),
        x: band * (index + 0.5) - barWidth / 2,
        y: top,
        height: Math.max(point.value ? 1.5 : 0, bottom - top),
      };
    });
    const guides: { y: number; value: number }[] = [];
    for (let value = min; value <= max + step / 2; value += step) {
      guides.push({ y: y(value), value });
    }
    const last = points.length - 1;
    const tickIndexes = Array.from(
      new Set([0, Math.round(last / 3), Math.round((last * 2) / 3), last])
    );
    return { bars, guides, band, barWidth, tickIndexes, zeroY: y(0) };
  }, [points, plotWidth, plotHeight, integer]);

  const activePoint = active !== undefined ? points[active] : undefined;
  useEffect(() => {
    onActiveChange?.(activePoint);
  }, [activePoint, onActiveChange]);
  const activeBar =
    active !== undefined && shape ? shape.bars[active] : undefined;

  const onPointer = (clientX: number, element: HTMLElement) => {
    if (!shape) return;
    const left = element.getBoundingClientRect().left;
    const index = Math.floor((clientX - left) / shape.band);
    setActive(Math.min(points.length - 1, Math.max(0, index)));
  };

  return (
    <Wrapper
      ref={measureRef}
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
      tabIndex={shape ? 0 : -1}
      onMouseMove={(event) => onPointer(event.clientX, event.currentTarget)}
      onMouseLeave={() => setActive(undefined)}
      onBlur={() => setActive(undefined)}
      onKeyDown={(event) => {
        if (!shape) return;
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();
          const step = event.key === "ArrowLeft" ? -1 : 1;
          setActive((current) =>
            Math.min(
              points.length - 1,
              Math.max(0, (current ?? points.length - 1) + step)
            )
          );
        }
      }}
    >
      {shape && (
        <svg width={width} height={height} aria-hidden={true}>
          {shape.guides.map((guide) => (
            <g key={guide.value}>
              <line
                x1={0}
                x2={plotWidth}
                y1={guide.y}
                y2={guide.y}
                stroke={tokens.hairlineStrong}
                strokeDasharray="2 4"
              />
              <AxisText x={plotWidth + 10} y={guide.y + 4} fill={tokens.muted}>
                {formatAxisValue(guide.value)}
              </AxisText>
            </g>
          ))}
          {activeBar && (
            <rect
              x={activeBar.center - shape.band / 2}
              y={TOP}
              width={shape.band}
              height={plotHeight}
              rx={4}
              fill={tokens.rowHover}
            />
          )}
          {shape.bars.map((bar, index) => (
            <rect
              key={points[index].time}
              x={bar.x}
              y={bar.y}
              width={shape.barWidth}
              height={bar.height}
              rx={Math.min(3, shape.barWidth / 3)}
              fill={
                active === index
                  ? tokens.accent
                  : hexToRGB(
                      tokens.accent,
                      active === undefined ? 0.72 : theme.isDarkMode ? 0.4 : 0.3
                    )
              }
            />
          ))}
          {shape.tickIndexes.map((index, position) => (
            <AxisText
              key={index}
              x={shape.bars[index].center}
              y={height - 6}
              fill={tokens.muted}
              textAnchor={
                shape.tickIndexes.length > 1 && position === 0
                  ? "start"
                  : position === shape.tickIndexes.length - 1 &&
                    shape.tickIndexes.length > 1
                  ? "end"
                  : "middle"
              }
              style={{ opacity: activeBar ? 0 : 1 }}
            >
              {formatTick(points[index].time)}
            </AxisText>
          ))}
        </svg>
      )}
      {activePoint && activeBar && (
        <TimeChip
          style={{
            left: Math.min(Math.max(activeBar.center, 56), plotWidth - 56),
            top: height - BOTTOM + 3,
          }}
        >
          {formatActiveTime(activePoint.time)}
        </TimeChip>
      )}
    </Wrapper>
  );
};

export default React.memo(BarsChart);

const AXIS_WIDTH = 52;
const TOP = 10;
const BOTTOM = 22;

/** Round guide steps (1, 2, 2.5, 5 × 10ⁿ; whole numbers for counts). */
const getScale = (low: number, high: number, integer?: boolean) => {
  const span = high - low || (integer ? 2 : 1);
  const raw = span / 2;
  const power = Math.pow(10, Math.floor(Math.log10(raw)));
  const factors = integer ? [1, 2, 5, 10] : [1, 2, 2.5, 5, 10];
  let step = power * (factors.find((factor) => factor * power >= raw) ?? 10);
  if (integer) step = Math.max(1, Math.round(step));
  const min = Math.floor(low / step) * step;
  // At least two steps, so a lone small bar doesn't fill the plot.
  const max = Math.max(min + 2 * step, Math.ceil(high / step) * step);
  return { min, max, step };
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  outline: none;
  cursor: crosshair;
  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => getNewLookTokens(p.theme).focus};
    border-radius: 6px;
  }
`;
const AxisText = styled.text`
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  transition: opacity 120ms;
`;
// Sits on the time axis under the crosshair, in place of the tick labels.
const TimeChip = styled.div`
  position: absolute;
  transform: translateX(-50%);
  padding: 1px 8px;
  border-radius: 6px;
  background: ${(p) => getNewLookTokens(p.theme).ink};
  color: ${(p) => getNewLookTokens(p.theme).panel};
  ${newLookType.meta};
  font-size: 11px;
  line-height: 16px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
`;
